import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { 
  FaArrowLeft, 
  FaBox, 
  FaCalendarAlt, 
  FaCreditCard,
  FaMapMarkerAlt,
  FaShippingFast,
  FaCheckCircle,
  FaClock,
  FaTimesCircle,
  FaTag,
  FaReceipt,
  FaShieldAlt,
  FaPhone,
  FaEnvelope,
  FaHome,
  FaGift,
  FaBarcode,
  FaTruck,
  FaEye
} from 'react-icons/fa';
import { HiOutlineSparkles } from 'react-icons/hi';
import { BsPrinter, BsDownload } from 'react-icons/bs';
import styled, { keyframes } from 'styled-components';
import Navbar from './Navbar';
import Footer from './Footer';

// Enhanced Keyframe Animations
const slideInFromLeft = keyframes`
  from {
    transform: translateX(-50px);
    opacity: 0;
  }
  to {
    transform: translateX(0);
    opacity: 1;
  }
`;

const slideInFromRight = keyframes`
  from {
    transform: translateX(50px);
    opacity: 0;
  }
  to {
    transform: translateX(0);
    opacity: 1;
  }
`;

const fadeInUp = keyframes`
  from {
    transform: translateY(30px);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
`;

const fadeInDown = keyframes`
  from {
    transform: translateY(-20px);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
`;

const pulse = keyframes`
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.05); }
`;

const shimmer = keyframes`
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
`;

const floatAnimation = keyframes`
  0%, 100% { transform: translateY(0px); }
  50% { transform: translateY(-8px); }
`;

const sparkle = keyframes`
  0%, 100% { opacity: 0; transform: scale(0.8) rotate(0deg); }
  50% { opacity: 1; transform: scale(1) rotate(180deg); }
`;

// Enhanced Styled Components
const PageContainer = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
  position: relative;
  overflow: hidden;
  
  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: 
      radial-gradient(circle at 20% 50%, rgba(99, 102, 241, 0.05) 0%, transparent 50%),
      radial-gradient(circle at 80% 20%, rgba(236, 72, 153, 0.05) 0%, transparent 50%),
      radial-gradient(circle at 40% 80%, rgba(14, 165, 233, 0.05) 0%, transparent 50%);
    animation: ${floatAnimation} 20s ease-in-out infinite;
  }
`;

const MainContent = styled.div`
  position: relative;
  z-index: 1;
  padding: 2rem;
  max-width: 1400px;
  margin: 0 auto;
  
  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

const BackButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 2rem;
  color: #1f2937;
  cursor: pointer;
  background: rgba(255, 255, 255, 0.9);
  border: 2px solid rgba(99, 102, 241, 0.2);
  font-size: 1rem;
  font-weight: 600;
  transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
  padding: 0.75rem 1.5rem;
  border-radius: 50px;
  backdrop-filter: blur(10px);
  animation: ${fadeInDown} 0.6s ease-out;
  box-shadow: 0 4px 15px rgba(99, 102, 241, 0.1);
  
  &:hover {
    background: rgba(99, 102, 241, 0.1);
    transform: translateX(-8px) translateY(-2px);
    box-shadow: 0 8px 25px rgba(99, 102, 241, 0.2);
    border-color: rgba(99, 102, 241, 0.3);
  }
  
  svg {
    transition: transform 0.3s ease;
  }
  
  &:hover svg {
    transform: translateX(-5px);
  }
`;

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
  gap: 1rem;
  animation: ${fadeInUp} 0.8s ease-out 0.2s both;
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
  }
`;

const PageTitle = styled.h1`
  font-size: 2.5rem;
  font-weight: 800;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  margin: 0;
  line-height: 1.2;
  position: relative;
  
  &:after {
    content: '📋';
    position: absolute;
    top: -5px;
    right: -35px;
    font-size: 1.8rem;
    animation: ${sparkle} 2s ease-in-out infinite;
  }
  
  @media (max-width: 768px) {
    font-size: 2rem;
    
    &:after {
      right: -30px;
      font-size: 1.5rem;
    }
  }
`;

const HeaderActions = styled.div`
  display: flex;
  gap: 1rem;
  
  @media (max-width: 768px) {
    width: 100%;
    justify-content: space-between;
  }
`;

const ActionButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.25rem;
  background: rgba(255, 255, 255, 0.9);
  color: #1f2937;
  border: 2px solid rgba(99, 102, 241, 0.2);
  border-radius: 50px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  backdrop-filter: blur(10px);
  box-shadow: 0 4px 15px rgba(99, 102, 241, 0.1);
  
  &:hover {
    background: rgba(99, 102, 241, 0.1);
    transform: translateY(-2px);
    box-shadow: 0 8px 20px rgba(99, 102, 241, 0.2);
  }
`;

const StatusBadge = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  background: ${props => props.bgColor};
  color: ${props => props.textColor};
  padding: 1rem 1.5rem;
  border-radius: 50px;
  font-size: 1rem;
  font-weight: 700;
  box-shadow: 0 8px 20px ${props => props.bgColor}40;
  animation: ${pulse} 2s ease-in-out infinite;
  border: 2px solid ${props => props.bgColor}60;
  
  svg {
    font-size: 1.2rem;
  }
`;

const OrderCard = styled.div`
  background: rgba(255, 255, 255, 0.95);
  border-radius: 24px;
  box-shadow: 
    0 20px 40px rgba(0, 0, 0, 0.08),
    0 0 0 1px rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(20px);
  overflow: hidden;
  margin-bottom: 2rem;
  animation: ${fadeInUp} 0.8s ease-out 0.4s both;
  position: relative;
  
  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.4), transparent);
    transition: left 0.8s ease;
  }
  
  &:hover:before {
    left: 100%;
  }
`;

const OrderSummaryHeader = styled.div`
  padding: 2rem;
  border-bottom: 2px solid rgba(99, 102, 241, 0.1);
  background: linear-gradient(135deg, #f8f9ff 0%, #e8f0ff 100%);
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 2rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 1.5rem;
  }
`;

const SummaryItem = styled.div`
  text-align: center;
  
  .label {
    font-size: 0.9rem;
    color: #667eea;
    margin-bottom: 0.5rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }
  
  .value {
    font-size: 1.3rem;
    font-weight: 700;
    color: #1f2937;
    
    &.large {
      font-size: 1.8rem;
      background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
    }
  }
`;

const TwoColumnLayout = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 2rem;
  padding: 2rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 2rem;
  }
`;

const InfoSection = styled.div`
  animation: ${props => props.animateFrom === 'left' ? slideInFromLeft : slideInFromRight} 0.8s ease-out 0.6s both;
`;

const SectionTitle = styled.h3`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  font-size: 1.3rem;
  font-weight: 700;
  color: #1f2937;
  margin-bottom: 1.5rem;
  position: relative;
  
  svg {
    color: #667eea;
    font-size: 1.4rem;
  }
  
  &:after {
    content: '';
    position: absolute;
    bottom: -8px;
    left: 0;
    width: 60px;
    height: 3px;
    background: linear-gradient(90deg, #667eea, #764ba2);
    border-radius: 3px;
  }
`;

const InfoCard = styled.div`
  background: linear-gradient(135deg, #f8f9ff 0%, #e8f0ff 100%);
  border-radius: 16px;
  padding: 1.5rem;
  border: 1px solid rgba(99, 102, 241, 0.1);
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(99, 102, 241, 0.15);
  }
`;

const InfoItem = styled.div`
  margin-bottom: 1rem;
  
  &:last-child {
    margin-bottom: 0;
  }
  
  .label {
    font-size: 0.85rem;
    color: #667eea;
    margin-bottom: 0.25rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }
  
  .value {
    font-size: 1rem;
    font-weight: 500;
    color: #1f2937;
    line-height: 1.5;
  }
`;

const ItemsSection = styled.div`
  padding: 2rem;
  border-top: 2px solid rgba(99, 102, 241, 0.1);
  animation: ${fadeInUp} 0.8s ease-out 0.8s both;
`;

const ItemsTable = styled.div`
  border: 1px solid rgba(99, 102, 241, 0.1);
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 4px 15px rgba(99, 102, 241, 0.05);
`;

const TableHeader = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr 1fr 1fr;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  padding: 1rem 1.5rem;
  font-weight: 700;
  font-size: 0.9rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    text-align: center;
    
    & > div:not(:first-child) {
      display: none;
    }
  }
`;

const ItemRow = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr 1fr 1fr;
  padding: 1.5rem;
  border-bottom: 1px solid rgba(99, 102, 241, 0.1);
  transition: all 0.3s ease;
  
  &:last-child {
    border-bottom: none;
  }
  
  &:hover {
    background: linear-gradient(135deg, #f8f9ff 0%, #e8f0ff 100%);
    transform: translateX(8px);
  }
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 1rem;
    text-align: center;
  }
`;

const ProductInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  
  @media (max-width: 768px) {
    flex-direction: column;
    text-align: center;
  }
`;

const ProductImage = styled.img`
  width: 80px;
  height: 80px;
  object-fit: cover;
  border-radius: 12px;
  border: 2px solid rgba(99, 102, 241, 0.1);
  transition: all 0.3s ease;
  
  &:hover {
    transform: scale(1.1) rotate(3deg);
    box-shadow: 0 8px 20px rgba(99, 102, 241, 0.3);
  }
`;

const ProductDetails = styled.div`
  flex: 1;
  
  .name {
    font-weight: 600;
    color: #1f2937;
    margin-bottom: 0.5rem;
    font-size: 1.05rem;
  }
  
  .brand {
    font-size: 0.85rem;
    color: #667eea;
    margin-bottom: 0.25rem;
    font-weight: 500;
  }
  
  .variant {
    font-size: 0.8rem;
    color: #6b7280;
    display: flex;
    align-items: center;
    gap: 0.5rem;
    flex-wrap: wrap;
  }
`;

const PriceInfo = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.25rem;
  
  .original {
    text-decoration: line-through;
    color: #9ca3af;
    font-size: 0.9rem;
  }
  
  .current {
    font-weight: 600;
    color: #1f2937;
    font-size: 1.05rem;
  }
  
  .discount {
    display: flex;
    align-items: center;
    gap: 0.25rem;
    color: #10b981;
    font-size: 0.8rem;
    font-weight: 600;
    background: rgba(16, 185, 129, 0.1);
    padding: 0.25rem 0.5rem;
    border-radius: 12px;
  }
`;

const QuantityBadge = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border-radius: 50%;
  font-weight: 700;
  font-size: 1.1rem;
  margin: 0 auto;
`;

const TotalCell = styled.div`
  display: flex;
  align-items: start;
  justify-content: flex-start;
  font-weight: 700;
  font-size: 1.1rem;
  color: #1f2937;
`;

const SummarySection = styled.div`
  padding: 2rem;
  border-top: 2px solid rgba(99, 102, 241, 0.1);
  background: linear-gradient(135deg, #f8f9ff 0%, #e8f0ff 100%);
  animation: ${fadeInUp} 0.8s ease-out 1s both;
`;

const SummaryGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 1rem;
  max-width: 400px;
  margin-left: auto;
  
  .label {
    color: #6b7280;
    font-weight: 500;
  }
  
  .value {
    text-align: right;
    font-weight: 600;
    color: #1f2937;
  }
  
  .total {
    color: #1f2937;
    font-weight: 800;
    font-size: 1.2rem;
    padding-top: 1rem;
    border-top: 2px solid rgba(99, 102, 241, 0.2);
    margin-top: 1rem;
    
    &.value {
      background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
      font-size: 1.4rem;
    }
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 4rem 2rem;
  background: rgba(255, 255, 255, 0.95);
  border-radius: 24px;
  backdrop-filter: blur(20px);
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
  animation: ${fadeInUp} 0.8s ease-out;
  
  .icon {
    font-size: 4rem;
    color: #d1d5db;
    margin-bottom: 1.5rem;
    animation: ${floatAnimation} 3s ease-in-out infinite;
  }
  
  h3 {
    font-size: 1.5rem;
    color: #1f2937;
    margin-bottom: 1rem;
  }
  
  p {
    color: #6b7280;
    margin-bottom: 2rem;
    font-size: 1.1rem;
  }
`;

const CTAButton = styled.button`
  padding: 1rem 2rem;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  border-radius: 50px;
  font-weight: 600;
  font-size: 1.1rem;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 8px 20px rgba(99, 102, 241, 0.3);
  
  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 12px 30px rgba(99, 102, 241, 0.4);
  }
`;

const OrderDetailsPage = () => {
  const navigate = useNavigate();
  const { state } = useLocation();
  const order = state?.order;

  if (!order) {
    return (
      <PageContainer>
        <MainContent>
          <BackButton onClick={() => navigate(-1)}>
            <FaArrowLeft /> Back to Orders
          </BackButton>
          <EmptyState>
            <FaReceipt className="icon" />
            <h3>Order Not Found</h3>
            <p>We couldn't find the order details you're looking for.</p>
            <CTAButton onClick={() => navigate('/orders')}>
              <HiOutlineSparkles style={{ marginRight: '0.5rem' }} />
              View All Orders
            </CTAButton>
          </EmptyState>
        </MainContent>
      </PageContainer>
    );
  }

  const getStatusStyles = (status) => {
    switch (status?.toLowerCase()) {
      case 'pending': 
        return { 
          bg: '#FEF3C7', 
          text: '#92400E', 
          icon: <FaClock /> 
        };
      case 'processing': 
        return { 
          bg: '#E0E7FF', 
          text: '#5B21B6', 
          icon: <FaClock /> 
        };
      case 'shipped': 
        return { 
          bg: '#DBEAFE', 
          text: '#1E40AF', 
          icon: <FaShippingFast /> 
        };
      case 'delivered': 
        return { 
          bg: '#D1FAE5', 
          text: '#065F46', 
          icon: <FaCheckCircle /> 
        };
      case 'cancelled': 
        return { 
          bg: '#FEE2E2', 
          text: '#991B1B', 
          icon: <FaTimesCircle /> 
        };
      default: 
        return { 
          bg: '#F3F4F6', 
          text: '#374151', 
          icon: <FaClock /> 
        };
    }
  };

  const statusStyles = getStatusStyles(order.status);

  return (
    <>
      <Navbar />
      <PageContainer>
        <MainContent>
          <BackButton onClick={() => navigate(-1)}>
            <FaArrowLeft /> Back to Orders
          </BackButton>
          
          <PageHeader>
            <PageTitle>Order Details</PageTitle>
            <HeaderActions>
             
              <StatusBadge
                bgColor={statusStyles.bg}
                textColor={statusStyles.text}
              >
                {statusStyles.icon}
                {order.status}
              </StatusBadge>
            </HeaderActions>
          </PageHeader>
          
          <OrderCard>
            <OrderSummaryHeader>
              <SummaryItem>
                <div className="label">Order Number</div>
                <div className="value">#{order.orderId || order.id}</div>
              </SummaryItem>
              
              <SummaryItem>
                <div className="label">Date Placed</div>
                <div className="value">
                  {new Date(order.date || order.createdAt).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'short',
                    day: 'numeric'
                  })}
                </div>
              </SummaryItem>
              
              <SummaryItem>
                <div className="label">Total Amount</div>
                <div className="value large">₹{order.total?.toFixed(2) || '0.00'}</div>
              </SummaryItem>
            </OrderSummaryHeader>
            
            <TwoColumnLayout>
              <InfoSection animateFrom="left">
                <SectionTitle>
                  <FaMapMarkerAlt />
                  Shipping Information
                </SectionTitle>
                
                {order.shippingAddress ? (
                  <InfoCard>
                    <InfoItem>
                      <div className="label">📦 Recipient</div>
                      <div className="value">
                        {order.shippingAddress.firstName} {order.shippingAddress.lastName}
                      </div>
                    </InfoItem>
                    
                    <InfoItem>
                      <div className="label">📞 Contact</div>
                      <div className="value">
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.25rem' }}>
                          <FaEnvelope style={{ color: '#667eea' }} />
                          {order.shippingAddress.email}
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                          <FaPhone style={{ color: '#667eea' }} />
                          {order.shippingAddress.phone}
                        </div>
                      </div>
                    </InfoItem>
                    
                    <InfoItem>
                      <div className="label">🏠 Address</div>
                      <div className="value">
                        {order.shippingAddress.address}<br />
                        {order.shippingAddress.city}, {order.shippingAddress.state}<br />
                        {order.shippingAddress.country}, {order.shippingAddress.zipCode}
                      </div>
                    </InfoItem>
                  </InfoCard>
                ) : (
                  <InfoCard>
                    <div style={{ color: '#6b7280', fontStyle: 'italic', textAlign: 'center', padding: '1rem' }}>
                      📍 No shipping information available
                    </div>
                  </InfoCard>
                )}
              </InfoSection>
              
              <InfoSection animateFrom="right">
                <SectionTitle>
                  <FaCreditCard />
                  Payment Information
                </SectionTitle>
                
                <InfoCard>
                  <InfoItem>
                    <div className="label">💳 Method</div>
                    <div className="value">
                      {order.paymentMethod?.type === 'credit-card' 
                        ? `Credit Card (•••• ${order.paymentMethod.cardLast4 || '••••'})`
                        : order.paymentMethod?.type || 'Cash on Delivery'}
                    </div>
                  </InfoItem>
                  
                  <InfoItem>
                    <div className="label">✅ Status</div>
                    <div className="value">
                      {order.paymentStatus || order.status || 'Pending'}
                    </div>
                  </InfoItem>
                  
                  <InfoItem>
                    <div className="label">💰 Amount Paid</div>
                    <div className="value">
                      ₹{order.total?.toFixed(2) || '0.00'}
                    </div>
                  </InfoItem>
                </InfoCard>
              </InfoSection>
            </TwoColumnLayout>
            
            <ItemsSection>
              <SectionTitle>
                <FaBox />
                Order Items ({Object.values(order.items || {}).length} items)
              </SectionTitle>
              
              <ItemsTable>
                <TableHeader>
                  <div>Product</div>
                  <div>Price</div>
                  <div>Quantity</div>
                  <div>Total</div>
                </TableHeader>
                
                {order.items && Object.values(order.items).map((item, index) => (
                  <ItemRow key={index}>
                    <ProductInfo>
                      <ProductImage 
                        src={item.image || '/placeholder-product.jpg'} 
                        alt={item.name}
                        onError={(e) => {
                          e.target.src = '/placeholder-product.jpg';
                        }}
                      />
                      <ProductDetails>
                        <div className="name">{item.name}</div>
                        {item.brand && <div className="brand">by {item.brand}</div>}
                        <div className="variant">
                          {item.selectedColor && (
                            <span>🎨 {item.selectedColor}</span>
                          )}
                          {item.selectedSize && (
                            <span>📏 {item.selectedSize}</span>
                          )}
                        </div>
                      </ProductDetails>
                    </ProductInfo>
                    
                    <PriceInfo>
                      {item.discount > 0 ? (
                        <>
                          <div className="original">₹{item.price?.toFixed(2)}</div>
                          <div className="current">₹{(item.discountedPrice || item.price)?.toFixed(2)}</div>
                          <div className="discount">
                            <FaTag /> {item.discount}% off
                          </div>
                        </>
                      ) : (
                        <div className="current">₹{(item.discountedPrice || item.price)?.toFixed(2)}</div>
                      )}
                    </PriceInfo>
                    
                    <QuantityBadge>
                      {item.quantity}
                    </QuantityBadge>
                    
                    <TotalCell>
                      ₹{((item.discountedPrice || item.price) * item.quantity)?.toFixed(2)}
                    </TotalCell>
                  </ItemRow>
                ))}
              </ItemsTable>
            </ItemsSection>
            
            <SummarySection>
              <SectionTitle>
                <FaReceipt />
                Order Summary
              </SectionTitle>
              
              <SummaryGrid>
                <div className="label">Subtotal:</div>
                <div className="value">₹{order.total?.toFixed(2) || '0.00'}</div>
                
                <div className="label">Shipping:</div>
                <div className="value">₹{order.shippingCost?.toFixed(2) || '0.00'}</div>
                
                <div className="label">Tax:</div>
                <div className="value">₹{order.tax?.toFixed(2) || '0.00'}</div>
                
                <div className="label total">Total:</div>
                <div className="value total">₹{order.total?.toFixed(2) || '0.00'}</div>
              </SummaryGrid>
            </SummarySection>
          </OrderCard>
        </MainContent>
      </PageContainer>
      <Footer />
    </>
  );
};

export default OrderDetailsPage;
